/**
 * PDF download for the PAYE HMRC claim letter. Laid out as a formal UK
 * business letter so it reads as correspondence, not a form dump.
 */

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces'
import { downloadBlob } from '@/helpers/downloadBlob'
import {
  claimLetterFilename,
  type ClaimLetterContent,
} from '@/helpers/claimTaxBack/claimLetter'

type PdfMakeOutput = {
  getBlob: () => Promise<Blob>
}

type PdfMakeClient = {
  addVirtualFileSystem?: (vfs: Record<string, string>) => void
  addFonts?: (fonts: Record<string, Record<string, string>>) => void
  vfs?: Record<string, string>
  createPdf: (definition: TDocumentDefinitions) => PdfMakeOutput
}

const pdfMakeClient = pdfMake as PdfMakeClient

const ROBOTO_FONT_DEFINITION = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
}

let pdfMakeFontsReady = false

function ensurePdfMakeFonts(): void {
  if (pdfMakeFontsReady) return
  const vfs = pdfFonts as Record<string, string>
  if (typeof pdfMakeClient.addVirtualFileSystem === 'function') {
    pdfMakeClient.addVirtualFileSystem(vfs)
    pdfMakeClient.addFonts?.(ROBOTO_FONT_DEFINITION)
  } else {
    pdfMakeClient.vfs = vfs
  }
  pdfMakeFontsReady = true
}

const INK = '#002d43'
const MUTED = '#5a7280'
const RULE = '#9bb0ba'
const HEADER_FILL = '#f4f8fa'

function senderBlock(letter: ClaimLetterContent): Content {
  const lines: Content[] = [
    {
      text: letter.senderName || '____________________',
      style: 'senderName',
    },
    ...letter.senderAddressLines.map((line) => ({
      text: line,
      style: 'sender',
    })),
    { text: letter.letterDate, style: 'date' },
  ]
  return {
    stack: lines,
    alignment: 'right',
    margin: [0, 0, 0, 18],
  }
}

function particularsTable(letter: ClaimLetterContent): Content {
  return {
    table: {
      widths: [168, '*'],
      body: letter.particulars.map((row) => [
        { text: row.label, style: 'particularLabel' },
        { text: row.value, style: 'particularValue' },
      ]),
    },
    layout: {
      hLineWidth: (i, node) =>
        i === 0 || i === node.table.body.length ? 0.8 : 0.35,
      vLineWidth: () => 0,
      hLineColor: (i, node) =>
        i === 0 || i === node.table.body.length ? INK : RULE,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 6,
      paddingBottom: () => 6,
    },
    margin: [0, 2, 0, 10],
  }
}

function cell(
  text: string,
  style: string,
  extras: { alignment?: 'left' | 'center' | 'right' | 'justify' } = {}
): TableCell {
  return { text, style, ...extras }
}

function donationsTable(letter: ClaimLetterContent): Content {
  const body: TableCell[][] = [
    [
      cell(letter.tableDate, 'scheduleHeader'),
      cell(letter.tableCharity, 'scheduleHeader'),
      cell(letter.tableAmount, 'scheduleHeader', { alignment: 'right' }),
    ],
    ...letter.donations.map((row, index) => [
      cell(row.date, index % 2 === 0 ? 'scheduleCell' : 'scheduleCellAlt'),
      cell(row.charity, index % 2 === 0 ? 'scheduleCell' : 'scheduleCellAlt'),
      cell(row.amount, index % 2 === 0 ? 'scheduleCell' : 'scheduleCellAlt', {
        alignment: 'right',
      }),
    ]),
    [
      cell('', 'scheduleTotal'),
      cell(letter.tableTotal, 'scheduleTotal', { alignment: 'right' }),
      cell(letter.amountGiven, 'scheduleTotal', { alignment: 'right' }),
    ],
  ]

  return {
    table: {
      headerRows: 1,
      keepWithHeaderRows: 1,
      widths: [88, '*', 88],
      body,
    },
    layout: {
      hLineWidth: (i, node) =>
        i === 0 || i === 1 || i === node.table.body.length ? 0.8 : 0,
      vLineWidth: () => 0,
      hLineColor: () => INK,
      fillColor: (rowIndex) => {
        if (rowIndex === 0) return HEADER_FILL
        if (rowIndex === letter.donations.length + 1) return HEADER_FILL
        return rowIndex % 2 === 0 ? '#f7fafb' : null
      },
      paddingLeft: (i) => (i === 0 ? 0 : 8),
      paddingRight: (i) => (i === 2 ? 0 : 8),
      paddingTop: () => 7,
      paddingBottom: () => 7,
    },
    margin: [0, 8, 0, 14],
  }
}

function signatureBlock(letter: ClaimLetterContent): Content[] {
  return [
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 168,
          y2: 0,
          lineWidth: 0.6,
          lineColor: RULE,
        },
      ],
      margin: [0, 36, 0, 8],
    },
    {
      text: letter.senderName || '____________________',
      style: 'signOffName',
    },
  ]
}

function scheduleIdentity(letter: ClaimLetterContent): Content {
  const rows: ClaimLetterContent['particulars'] = [
    { label: 'Name', value: letter.senderName || '____________________' },
    ...letter.particulars.filter(
      (row) =>
        row.label === 'National Insurance number' || row.label === 'Tax year'
    ),
    { label: 'Total Gift Aided donations', value: letter.amountGiven },
  ]
  return {
    table: {
      widths: [168, '*'],
      body: rows.map((row) => [
        { text: row.label, style: 'particularLabel' },
        { text: row.value, style: 'particularValue' },
      ]),
    },
    layout: {
      hLineWidth: (i, node) =>
        i === 0 || i === node.table.body.length ? 0.8 : 0.35,
      vLineWidth: () => 0,
      hLineColor: (i, node) =>
        i === 0 || i === node.table.body.length ? INK : RULE,
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 6,
      paddingBottom: () => 6,
    },
    margin: [0, 4, 0, 12],
  }
}

function schedulePage(letter: ClaimLetterContent): Content[] {
  return [
    {
      columns: [
        { text: letter.scheduleEyebrow, style: 'scheduleEyebrow' },
        {
          text: letter.scheduleFooter,
          style: 'scheduleMeta',
          alignment: 'right',
        },
      ],
      pageBreak: 'before',
      margin: [0, 0, 0, 10],
    },
    {
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 451,
          y2: 0,
          lineWidth: 1,
          lineColor: INK,
        },
      ],
      margin: [0, 0, 0, 16],
    },
    { text: letter.scheduleHeading, style: 'scheduleTitle', margin: [0, 0, 0, 4] },
    {
      text: letter.scheduleSubheading,
      style: 'scheduleMeta',
      margin: [0, 0, 0, 10],
    },
    { text: letter.scheduleIntro, style: 'body', margin: [0, 0, 0, 14] },
    scheduleIdentity(letter),
    { text: letter.scheduleCount, style: 'scheduleMeta', margin: [0, 0, 0, 8] },
    donationsTable(letter),
  ]
}

function buildClaimLetterDocumentDefinition(
  letter: ClaimLetterContent
): TDocumentDefinitions {
  const content: Content[] = [
    senderBlock(letter),
    { text: letter.greeting, style: 'body', margin: [0, 0, 0, 12] },
    { text: letter.subject, style: 'subject', margin: [0, 0, 0, 12] },
    { text: letter.opening, style: 'body', margin: [0, 0, 0, 10] },
    { text: letter.totalGiven, style: 'body', margin: [0, 0, 0, 10] },
    { text: letter.rateClaim, style: 'body', margin: [0, 0, 0, 14] },
    {
      text: letter.particularsHeading,
      style: 'sectionHeading',
      margin: [0, 2, 0, 4],
    },
    particularsTable(letter),
  ]

  if (letter.enclosureNote) {
    content.push({
      text: letter.enclosureNote,
      style: 'body',
      margin: [0, 4, 0, 12],
    })
  }

  content.push({
    text: letter.giftAidCover,
    style: 'body',
    margin: [0, 0, 0, 10],
  })
  content.push({
    text: letter.request,
    style: 'body',
    margin: [0, 0, 0, 18],
  })
  content.push({
    text: letter.closing,
    style: 'body',
    margin: [0, 0, 0, 8],
  })
  content.push(...signatureBlock(letter))

  if (letter.preparedNote) {
    content.push({
      text: letter.preparedNote,
      style: 'footerNote',
      margin: [0, 24, 0, 0],
    })
  }

  if (letter.includeSchedule) {
    content.push(...schedulePage(letter))
  }

  return {
    pageSize: 'A4',
    pageMargins: [72, 64, 72, 56],
    defaultStyle: { font: 'Roboto', fontSize: 11, color: INK },
    footer: (currentPage, pageCount) => {
      // The letter itself is unnumbered. Donation schedule pages start at 1.
      if (!letter.includeSchedule || currentPage < 2) {
        return { text: '', margin: [0, 0, 0, 0] }
      }
      const schedulePage = currentPage - 1
      const scheduleCount = Math.max(pageCount - 1, 1)
      return {
        text: `${schedulePage} of ${scheduleCount}`,
        style: 'pageFooter',
        alignment: 'right',
        margin: [72, 0, 72, 24],
      }
    },
    styles: {
      senderName: { fontSize: 11, bold: true, lineHeight: 1.3, color: INK },
      sender: { fontSize: 10, lineHeight: 1.3, color: INK },
      date: { fontSize: 10, lineHeight: 1.3, color: INK, margin: [0, 10, 0, 0] },
      subject: { fontSize: 11, bold: true, color: INK },
      sectionHeading: { fontSize: 11, bold: true, color: INK },
      body: { fontSize: 11, lineHeight: 1.45, color: INK },
      particularLabel: { fontSize: 10, color: MUTED },
      particularValue: { fontSize: 10, bold: true, color: INK },
      scheduleEyebrow: {
        fontSize: 9,
        bold: true,
        color: MUTED,
        characterSpacing: 1.2,
      },
      scheduleTitle: { fontSize: 16, bold: true, color: INK },
      scheduleMeta: { fontSize: 9.5, color: MUTED },
      scheduleHeader: { fontSize: 9, bold: true, color: INK },
      scheduleCell: { fontSize: 9.5, color: INK },
      scheduleCellAlt: { fontSize: 9.5, color: INK },
      scheduleTotal: { fontSize: 10, bold: true, color: INK },
      signOffName: { fontSize: 11, bold: true, color: INK },
      footerNote: { fontSize: 9, italics: true, color: MUTED },
      pageFooter: { fontSize: 8, color: MUTED },
    },
    content,
  }
}

export async function downloadClaimLetterPdf(
  letter: ClaimLetterContent
): Promise<void> {
  ensurePdfMakeFonts()
  const doc = buildClaimLetterDocumentDefinition(letter)
  const pdf = pdfMakeClient.createPdf(doc)
  const blob = await pdf.getBlob()
  if (!blob || blob.size === 0) {
    throw new Error('Generated claim letter PDF is empty')
  }
  downloadBlob(blob, claimLetterFilename(letter.yearLabel, 'pdf'))
}
